import React from 'react'
import t from 'format-message'

import {NotFound} from '@instructure/quiz-pages/NotFound/index'
import {QuizUnavailable} from './quizUnavailable'
import {createBrowserRouter, useRouteError} from 'react-router'
import {buildRoutes} from './building/router'
import {moderationRoutes} from './moderation/router'
import {gradingRoutes} from './grading/router'
import {reportingRoutes} from './reporting/router'
import {banksRoutes} from './banks/router'
import {exportsRoutes} from './contentExports/router'
import {errorsRoutes} from './errors/router'
import {versionRoutes} from './version/router'
import {useDocumentTitle} from './common/lib/useDocumentTitle'
import {takingRoutes} from './taking/router'
import {observingRoutes} from './observing/router'
import {TeacherLayout} from './building/components/navigation/layout/TeacherLayout'
import {previewRoutes} from './building/components/pages/Preview/router'
import {Errors} from './errors/components/pages/Errors'

function CourseConcludedPage() {
  useDocumentTitle(t('Course Concluded!'))

  return (
    <QuizUnavailable
      message={t('This quiz is no longer available as the course has been concluded.')}
    />
  )
}

function NotFoundPage() {
  useDocumentTitle(t('Not Found'))

  return <NotFound />
}

function ErrorBoundary({basename}) {
  const error = useRouteError()
  const status = error?.status ? String(error.status) : null

  return <Errors status={status} showHomeButton={true} baseUrl={basename} />
}

/**
 * @type {import('react-router').RouteObject[]}
 */
export const routes = [
  {
    path: '/banks',
    children: banksRoutes,
  },
  {
    element: <TeacherLayout />,
    children: [
      {
        path: '/build/:activeAssignmentId',
        children: buildRoutes,
      },
      {
        path: '/reporting/:activeAssignmentId',
        children: reportingRoutes,
      },
      {
        path: '/moderation/:activeAssignmentId',
        children: moderationRoutes,
      },
      {
        path: '/exports/:activeAssignmentId',
        children: exportsRoutes,
      },
    ],
  },
  {
    path: '/build/:activeAssignmentId/preview',
    children: previewRoutes,
  },
  {
    path: '/taking/:activeParticipantSessionId',
    children: takingRoutes,
  },
  {
    path: '/observing/:activeAssignmentId',
    children: observingRoutes,
  },
  {
    path: '/moderation/:activeAssignmentId/grading/:activeParticipantSessionId',
    children: gradingRoutes,
  },
  {
    path: '/errors/:status',
    children: errorsRoutes,
  },
  {
    path: '/version',
    children: versionRoutes,
  },
  {
    path: '/course_concluded',
    element: <CourseConcludedPage />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]

/**
 * @param {string} [basename] - The base URL for the router.
 * @returns {import('react-router').RouterProviderProps['router']} An object representing the browser router.
 */
export function createRouter(basename = null) {
  const routesWithErrorBoundary = [
    {
      errorElement: <ErrorBoundary basename={basename} />,
      children: routes,
    },
  ]

  return createBrowserRouter(routesWithErrorBoundary, {basename})
}
